import React, { useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";

import Header from "../../../../../../components/wizard/Header";
import Footer from "../../../../../../components/wizard/Footer/Footer";
import Image from "../../../../../../components/capture/ReceivingWizard/Image";
import ImageUploadButtons from "../../../../../capture/Images/ImageUploadButtons";
import AutoExpandInput from "../../../../../../components/form/AutoExpandInput";
import SerialNumbersEditorModal from "../../../../../../components/form/SerialNumbersEditorModal";
import UnpickedItemsErrorModal from "./UnpickedItemsErrorModal";
import useMutation from "../../../../../../hooks/useMutation";
import useNotification from "../../../../../../hooks/notification";
import useAuth from "../../../../../../hooks/useAuth";
import { getWO } from "../../../../utils";
import useHasAdditionalCharges from "../../../../../../components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";
import { backFromCharges } from "src/utils/integrationWizardSteps";
import {
  ADD_INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENT_MUTATION,
  REMOVE_INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENT_MUTATION,
  UPDATE_INVENTORY_REQUEST_MUTATION,
} from "../../../../../../constants/graphql/mutations";
import { INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENTS_QUERY } from "../../../../../../constants/graphql/queries";
import { serverFormatSeconds } from "../../../../../../utils/dates";

const trackingDetailsToText = (trackingDetails = []) =>
  trackingDetails
    .map((entry) => entry?.tracking_number || entry?.trackingNumber || "")
    .filter(Boolean)
    .join("\n");

const textToTrackingDetails = (value = "") =>
  value
    .split(/\n|,/)
    .map((trackingNumber) => trackingNumber.trim())
    .filter(Boolean)
    .map((trackingNumber) => ({ tracking_number: trackingNumber }));

export default function FinalReleasePhotos({
  project,
  inventoryRequest,
  onClose,
  verticalLayout = false,
}) {
  const { company } = useAuth();
  const navigate = useNavigate();
  const { notifyError } = useNotification();
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound_enabled");
  const { hasIntegration } = useHasEnabledIntegration(project);
  const [notes, setNotes] = useState(inventoryRequest?.finalReleaseNotes || "");
  const [trackingText, setTrackingText] = useState(trackingDetailsToText(inventoryRequest?.trackingDetails));
  const [isNotesFocused, setIsNotesFocused] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [unpickedErrorOpen, setUnpickedErrorOpen] = useState(false);
  const requiresTracking = !!project?.settings?.requires_outbound_tracking_for_completion;

  const { data: { inventoryRequestFinalReleaseAttachments = [] } = {} } = useQuery(
    INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENTS_QUERY,
    {
      variables: { inventoryRequestId: inventoryRequest?.id },
      skip: !inventoryRequest?.id,
    }
  );

  const [addAttachment, { loading: adding }] = useMutation(
    ADD_INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENT_MUTATION
  );
  const [removeAttachment, { loading: removing }] = useMutation(
    REMOVE_INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENT_MUTATION
  );
  const [updateInventoryRequest, { loading: updating }] = useMutation(
    UPDATE_INVENTORY_REQUEST_MUTATION
  );

  const handleUpload = files => {
    const file = files[0];
    addAttachment({
      variables: { inventoryRequestId: inventoryRequest.id, file },
      refetchQueries: [
        {
          query: INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENTS_QUERY,
          variables: { inventoryRequestId: inventoryRequest.id },
        },
      ],

      onFailure: errors => {
        notifyError(errors);
      },
    });
  };

  const handleRemoveImage = data => {
    removeAttachment({
      ...data,
      variables: {
        inventoryRequestId: inventoryRequest.id,
        ...data.variables,
      },
      refetchQueries: [
        {
          query: INVENTORY_REQUEST_FINAL_RELEASE_ATTACHMENTS_QUERY,
          variables: { inventoryRequestId: inventoryRequest.id },
        },
      ],
    });
  };

  const handleNotesChange = event => {
    setNotes(event.target.value);
  };

  const handleTrackingChipClick = () => {
    setTrackingModalOpen(true);
  };

  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    if (hasAdditionalCharges) {
      params.set("step", "charges");
    } else {
      params.set("step", backFromCharges({ hasIntegration }));
    }
    navigate({ search: params.toString() });
  };

  const handleNext = async () => {
    const inventoryItems = inventoryRequest?.inventoryItems || [];
    const hasUnpickedItems = inventoryItems.some(item => !item.pickedStatus);
    if (hasUnpickedItems) {
      setUnpickedErrorOpen(true);
      return;
    }

    const notesChanged = notes !== (inventoryRequest?.finalReleaseNotes || "");
    const notesInput = notesChanged ? { finalReleaseNotes: notes } : {};
    const trackingDetails = textToTrackingDetails(trackingText);
    const trackingChanged = trackingText !== trackingDetailsToText(inventoryRequest?.trackingDetails);
    const trackingInput = trackingChanged ? { trackingDetails } : {};

    if (verticalLayout) {
      const input = { ...notesInput, ...trackingInput };
      if (!inventoryRequest.pickingCompleteTime) {
        input.pickingCompleteTime = moment().format(serverFormatSeconds);
      }
      if (Object.keys(input).length > 0) {
        await updateInventoryRequest({ variables: { id: inventoryRequest.id, input } });
      }
      onClose();
      return;
    }

    const input = { ...notesInput, ...trackingInput };
    if (
      company?.settings?.auto_complete_picked_inventory_requests &&
      (!requiresTracking || trackingDetails.length > 0)
    ) {
      input.vaStatus = { completed: true };
    }
    if (!inventoryRequest.pickingCompleteTime) {
      input.pickingCompleteTime = moment().format(serverFormatSeconds);
    }
    if (Object.keys(input).length > 0) {
      await updateInventoryRequest({
        variables: { id: inventoryRequest.id, input },
      });
    }

    const params = new URLSearchParams(window.location.search);
    params.set("step", "confirmation");
    navigate({ search: params.toString() });
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
      <Header title="Final Release" info={getWO(inventoryRequest)} />
      <Box height={3}>{(removing || adding) && <LinearProgress />}</Box>
      <Box
        px={2}
        mt={2}
        flexGrow={1}
        display="flex"
        flexDirection="column"
        sx={{ minHeight: 320, position: "relative" }}
      >
        <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Image
                images={inventoryRequestFinalReleaseAttachments}
                onUpload={handleUpload}
                onRemove={handleRemoveImage}
                helperText="Add release photos to show how the order was handed off or delivered"
              />
            </Grid>
            <Grid size={12}>
              <Box my={1} display="flex" alignItems="center" flexDirection="column">
                <Box width="100%" maxWidth={400}>
                  <ImageUploadButtons
                    onUpload={e => handleUpload(e.target.files)}
                    idPrefix="final-release"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Grid size={12}>
            <AutoExpandInput
              label="Optional Notes"
              size="small"
              variant="outlined"
              value={notes}
              onChange={handleNotesChange}
              onFocus={() => setIsNotesFocused(true)}
              onBlur={() => setIsNotesFocused(false)}
              showNAButton={false}
              InputProps={{
                endAdornment: requiresTracking && isNotesFocused && !notes ? (
                  <InputAdornment position="end">
                    <Chip
                      label="Tracking #"
                      size="small"
                      clickable
                      color="primary"
                      variant="outlined"
                      onMouseDown={handleTrackingChipClick}
                      sx={{ cursor: "pointer" }}
                    />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          {requiresTracking && (
            <Grid size={12}>
              <AutoExpandInput
                label="Tracking Numbers Required"
                size="small"
                variant="outlined"
                value={trackingText}
                onClick={() => setTrackingModalOpen(true)}
                helperText="Click to add or edit tracking numbers"
                multiline
                showNAButton={false}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box mt={2}>
        <Footer
          step={hasAdditionalCharges ? 3 : 2}
          steps={hasAdditionalCharges ? 4 : 3}
          startTime={inventoryRequest?.pickingStartTime}
          completeTime={inventoryRequest?.pickingCompleteTime}
          showRaceMode={!verticalLayout}
        >
          <div style={{ width: verticalLayout ? "100%" : "auto" }}>
            {!verticalLayout && (
              <Button onClick={handleBack} variant="outlined" sx={{ mr: 1, textTransform: "none" }}>
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{ textTransform: "none" }}
              fullWidth={verticalLayout}
              disabled={updating || removing || adding}
            >
              Submit
            </Button>
          </div>
        </Footer>
      </Box>
      <SerialNumbersEditorModal
        open={trackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        title="Tracking Numbers"
        fieldLabel="Tracking Number"
        addButtonLabel="+ Add Tracking Number"
        scanTooltip="Scan Tracking Number"
        initialSerials={trackingText.split("\n").filter(Boolean)}
        onSave={(trackingNumbers) => setTrackingText(trackingNumbers.join("\n"))}
      />
      <UnpickedItemsErrorModal
        open={unpickedErrorOpen}
        onClose={() => {
          setUnpickedErrorOpen(false);
          const params = new URLSearchParams(window.location.search);
          params.set("step", "items");
          navigate({ search: params.toString() });
        }}
        onRemoveItems={() => {
          setUnpickedErrorOpen(false);
          const params = new URLSearchParams(window.location.search);
          params.set("step", "items");
          params.set("returnItems", "1");
          navigate({ search: params.toString() });
        }}
      />
    </Box>
  );
}
